import { useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  AppWindow,
  ArrowDownRight,
  ArrowUpRight,
  Download,
  Filter,
  Link2,
  List,
  Loader2,
  MapPin,
  Monitor,
  MoreVertical,
  Server,
  Share2,
  Tag,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useClientStore } from '@/client/stores/client';
import { useDataScopeStore } from '@/client/stores/data-scope';
import { getApiErrorMessage } from '@/client/utils/errors';
import { toInputDate } from '@/client/utils/dashboard-trends';
import {
  useVisitorBreakdown,
  useVisitorJourney,
  useVisitorProviders,
  useVisitorSummary,
  visitorBreakdownPath,
  visitorParams,
  VisitorMetric,
  VisitorProvider,
  VisitorQuery,
  VisitorRow,
} from '@/client/api/analytics/visitors.query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const DAY_MS = 86_400_000;
const PRESETS = [
  { id: 'today', label: 'Today', days: 1 },
  { id: '7d', label: '7d', days: 7 },
  { id: '28d', label: '28d', days: 28 },
  { id: '91d', label: '91d', days: 91 },
];

const compact = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 });
const duration = (s: number) => (s >= 60 ? `${Math.floor(s / 60)}m ${Math.round(s % 60)}s` : `${Math.round(s)}s`);

const METRICS: { key: VisitorMetric; label: string; fmt: (v: number) => string; lowerIsBetter?: boolean }[] = [
  { key: 'visitors', label: 'Unique visitors', fmt: compact.format },
  { key: 'visits', label: 'Total visits', fmt: compact.format },
  { key: 'pageviews', label: 'Total pageviews', fmt: compact.format },
  { key: 'views_per_visit', label: 'Views per visit', fmt: (v) => v.toFixed(2) },
  { key: 'bounce_rate', label: 'Bounce rate', fmt: (v) => `${Math.round(v)}%`, lowerIsBetter: true },
  { key: 'visit_duration', label: 'Visit duration', fmt: duration },
];

const DIM_LABEL: Record<string, string> = {
  channel: 'Channel',
  source: 'Source',
  campaign: 'Campaign',
  referrer: 'Referrer',
  page: 'Page',
  entry_page: 'Entry page',
  exit_page: 'Exit page',
  country: 'Country',
  region: 'Region',
  city: 'City',
  browser: 'Browser',
  os: 'Operating system',
  device: 'Screen size',
};

/** The four breakdown cards, and the same grouping for the Filter menu. */
const PANELS = [
  { title: 'Sources', icon: Share2, tabs: ['channel', 'source', 'campaign', 'referrer'], initial: 'source' },
  { title: 'Pages', icon: Link2, tabs: ['page', 'entry_page', 'exit_page'], initial: 'page' },
  { title: 'Locations', icon: MapPin, tabs: ['country', 'region', 'city'], initial: 'country' },
  { title: 'Devices', icon: Monitor, tabs: ['browser', 'os', 'device'], initial: 'browser' },
];
const FILTER_ICON: Record<string, typeof Link2> = {
  page: Link2, entry_page: Link2, exit_page: Link2,
  channel: Share2, source: Share2, referrer: Share2, campaign: Tag,
  country: MapPin, region: MapPin, city: MapPin,
  device: Monitor, browser: AppWindow, os: Server,
};

const regionNames = new Intl.DisplayNames(undefined, { type: 'region' });
function rowLabel(dimension: string, row: VisitorRow): string {
  // Native stores the two-letter code; Plausible already sends the name.
  if (dimension === 'country' && /^[A-Z]{2}$/.test(row.name)) {
    try {
      return regionNames.of(row.name) ?? row.name;
    } catch {
      return row.name;
    }
  }
  return row.name;
}

function tickLabel(t: string, hourly: boolean): string {
  if (hourly) return t.slice(11, 16);
  const [y, m, d] = t.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

// ---------------------------------------------------------------------------
// Breakdown card
// ---------------------------------------------------------------------------
function Rows({
  rows,
  dimension,
  onPick,
}: {
  rows: VisitorRow[];
  dimension: string;
  onPick?: (row: VisitorRow) => void;
}) {
  const max = Math.max(...rows.map((r) => r.visitors), 1);
  return (
    <ul className="space-y-1">
      {rows.map((row) => (
        <li key={row.value}>
          <button
            type="button"
            onClick={() => onPick?.(row)}
            title={onPick ? `Filter by ${rowLabel(dimension, row)}` : undefined}
            className="group relative flex w-full items-center justify-between gap-3 rounded px-2 py-1.5 text-left text-sm hover:bg-gray-50"
          >
            <span
              className="absolute inset-y-0 left-0 rounded bg-indigo-50 group-hover:bg-indigo-100"
              style={{ width: `${(row.visitors / max) * 100}%` }}
            />
            <span className="relative truncate text-gray-800">{rowLabel(dimension, row)}</span>
            <span className="relative shrink-0 font-medium tabular-nums text-gray-700">
              {compact.format(row.visitors)}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function RowsSkeleton() {
  return (
    <div className="space-y-2" aria-label="Loading">
      {[90, 70, 55, 40, 30, 20].map((w) => (
        <Skeleton key={w} className="h-7" style={{ width: `${w}%` }} />
      ))}
    </div>
  );
}

async function downloadCsv(query: VisitorQuery, dimension: string) {
  try {
    const res = await fetch(`${visitorBreakdownPath}?${visitorParams(query, { dimension, format: 'csv' })}`, {
      credentials: 'include',
      cache: 'no-store',
      headers: {
        ...((useClientStore.getState().defaultHeaders ?? {}) as Record<string, string>),
        'x-admin-data-scope': useDataScopeStore.getState().scope,
      },
    });
    if (!res.ok) throw new Error(`Export failed (${res.status}).`);
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = `visitors-${query.provider}-${dimension}-${query.from}-to-${query.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error(getApiErrorMessage(e, 'Export failed'), { richColors: true });
  }
}

function BreakdownCard({
  panel,
  provider,
  query,
  onFilter,
}: {
  panel: (typeof PANELS)[number];
  provider: VisitorProvider;
  query: VisitorQuery;
  onFilter: (dimension: string, row: VisitorRow) => void;
}) {
  const tabs = panel.tabs.filter((t) => provider.dimensions.includes(t));
  const [picked, setPicked] = useState(panel.initial);
  const dimension = tabs.includes(picked) ? picked : tabs[0];
  const [showAll, setShowAll] = useState(false);
  const { data, isLoading, error } = useVisitorBreakdown(query, dimension, 9, Boolean(dimension));
  const all = useVisitorBreakdown(query, dimension, 100, showAll && Boolean(dimension));

  if (!dimension) return null;
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-2 border-b border-gray-100">
        <div className="flex gap-4 overflow-x-auto">
          {tabs.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setPicked(t)}
              className={cn(
                '-mb-px whitespace-nowrap border-b-2 pb-2 text-xs font-semibold uppercase tracking-wide',
                t === dimension
                  ? 'border-indigo-600 text-gray-900'
                  : 'border-transparent text-gray-400 hover:text-gray-600',
              )}
            >
              {DIM_LABEL[t]}
            </button>
          ))}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="mb-2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              aria-label={`${panel.title} actions`}
            >
              <MoreVertical className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setShowAll(true)}>
              <List className="mr-2 h-4 w-4" /> View top 100
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => downloadCsv(query, dimension)}>
              <Download className="mr-2 h-4 w-4" /> Export CSV
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="mb-1 flex justify-between px-2 text-[11px] text-gray-400">
        <span>{DIM_LABEL[dimension]}</span>
        <span>Visitors</span>
      </div>
      <div className="min-h-[232px]">
        {isLoading ? (
          <RowsSkeleton />
        ) : error ? (
          <p className="px-2 py-8 text-center text-sm text-red-600">{getApiErrorMessage(error)}</p>
        ) : data && data.length > 0 ? (
          <Rows rows={data} dimension={dimension} onPick={(row) => onFilter(dimension, row)} />
        ) : (
          <p className="px-2 py-16 text-center text-sm text-gray-400">
            No {DIM_LABEL[dimension].toLowerCase()} data in this range
          </p>
        )}
      </div>

      <Dialog open={showAll} onOpenChange={setShowAll}>
        <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {DIM_LABEL[dimension]} · {query.from} to {query.to}
            </DialogTitle>
          </DialogHeader>
          {all.isLoading ? (
            <RowsSkeleton />
          ) : (
            <Rows
              rows={all.data ?? []}
              dimension={dimension}
              onPick={(row) => {
                onFilter(dimension, row);
                setShowAll(false);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filter menu: pick a dimension, then one of its values in this range
// ---------------------------------------------------------------------------
function FilterValues({
  query,
  dimension,
  onPick,
}: {
  query: VisitorQuery;
  dimension: string;
  onPick: (row: VisitorRow) => void;
}) {
  const { data, isLoading } = useVisitorBreakdown(query, dimension, 15);
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading values
      </div>
    );
  }
  if (!data?.length) return <div className="px-2 py-1.5 text-sm text-gray-400">Nothing in this range</div>;
  return (
    <>
      {data.map((row) => (
        <DropdownMenuItem key={row.value} onClick={() => onPick(row)} className="justify-between gap-6">
          <span className="max-w-[220px] truncate">{rowLabel(dimension, row)}</span>
          <span className="text-xs tabular-nums text-gray-400">{compact.format(row.visitors)}</span>
        </DropdownMenuItem>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Journeys: where people went after a page
// ---------------------------------------------------------------------------
function JourneyColumn({
  title,
  rows,
  loading,
  selected,
  onPick,
  empty,
}: {
  title: string;
  rows?: VisitorRow[];
  loading: boolean;
  selected?: string;
  onPick: (value: string) => void;
  empty: string;
}) {
  return (
    <div className="rounded-md border border-gray-200 p-3">
      <div className="mb-2 text-xs font-semibold text-gray-700">{title}</div>
      <div className="min-h-[200px]">
        {loading ? (
          <RowsSkeleton />
        ) : rows?.length ? (
          <ul className="space-y-1">
            {rows.map((row) => (
              <li key={row.value}>
                <button
                  type="button"
                  onClick={() => onPick(row.value)}
                  className={cn(
                    'flex w-full items-center justify-between gap-3 rounded px-2 py-1.5 text-left text-sm',
                    selected === row.value ? 'bg-indigo-600 text-white' : 'bg-indigo-50 text-gray-800 hover:bg-indigo-100',
                  )}
                >
                  <span className="truncate">{row.name}</span>
                  <span className="shrink-0 font-medium tabular-nums">{compact.format(row.visitors)}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="py-16 text-center text-sm text-gray-400">{empty}</p>
        )}
      </div>
    </div>
  );
}

function Journeys({ query }: { query: VisitorQuery }) {
  const [steps, setSteps] = useState<string[]>([]);
  const starts = useVisitorBreakdown(query, 'page', 10);
  const first = useVisitorJourney(query, steps.slice(0, 1));
  const second = useVisitorJourney(query, steps.length > 1 ? steps.slice(0, 2) : []);
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Explore user journeys</h2>
          <p className="text-xs text-gray-500">Pick a page to see where visitors went straight after it.</p>
        </div>
        {steps.length > 0 && (
          <button type="button" onClick={() => setSteps([])} className="text-xs text-indigo-600 hover:underline">
            Reset
          </button>
        )}
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <JourneyColumn
          title="Starting point"
          rows={starts.data}
          loading={starts.isLoading}
          selected={steps[0]}
          onPick={(v) => setSteps([v])}
          empty="No pages in this range"
        />
        <JourneyColumn
          title="1 step after"
          rows={steps.length ? first.data : undefined}
          loading={steps.length > 0 && first.isLoading}
          selected={steps[1]}
          onPick={(v) => setSteps([steps[0], v])}
          empty={steps.length ? 'Nobody went further' : 'Select a starting point'}
        />
        <JourneyColumn
          title="2 steps after"
          rows={steps.length > 1 ? second.data : undefined}
          loading={steps.length > 1 && second.isLoading}
          onPick={() => undefined}
          empty={steps.length > 1 ? 'Nobody went further' : 'Select a page to continue'}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const VisitorInsightsPage = () => {
  useBreadcrumbs([{ label: 'Analytics', href: '/admin/analytics' }, { label: 'Visitor Insights' }]);

  const providers = useVisitorProviders();
  const [providerKey, setProviderKey] = useState('native');
  const [to, setTo] = useState(() => toInputDate(new Date()));
  const [from, setFrom] = useState(() => toInputDate(new Date(Date.now() - 27 * DAY_MS)));
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [metric, setMetric] = useState<VisitorMetric>('visitors');

  const provider = providers.data?.find((p) => p.key === providerKey);
  const query: VisitorQuery = useMemo(
    () => ({ provider: providerKey, from, to, filters }),
    [providerKey, from, to, filters],
  );
  const summary = useVisitorSummary(query, Boolean(provider?.connected));

  const today = toInputDate(new Date());
  const activePreset = PRESETS.find(
    (p) => to === today && from === toInputDate(new Date(Date.now() - (p.days - 1) * DAY_MS)),
  )?.id;
  const setFilter = (dimension: string, row: VisitorRow) =>
    setFilters((prev) => ({ ...prev, [dimension]: row.value }));
  const switchProvider = (key: string) => {
    setProviderKey(key);
    setFilters({}); // values are not the same across sources ("direct" vs "Direct / None")
  };

  const active = METRICS.find((m) => m.key === metric)!;
  const hourly = summary.data?.interval === 'hour';
  const chart = (summary.data?.series ?? []).map((p) => ({ label: tickLabel(p.t, hourly), value: p[metric] }));

  return (
    <div className="min-h-screen space-y-4 bg-gray-50 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Visitor Insights</h1>
          <p className="text-xs text-gray-500">Who visits, where they come from and what they look at</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {provider?.connected && summary.data && summary.data.realtime !== null && (
            <span className="inline-flex items-center gap-1.5 text-xs text-gray-600">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              {summary.data.realtime} current visitor{summary.data.realtime === 1 ? '' : 's'}
            </span>
          )}

          {provider?.connected && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  <Filter className="h-3.5 w-3.5" /> Filter
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                {PANELS.flatMap((p) => p.tabs)
                  .filter((d) => provider.dimensions.includes(d))
                  .map((d) => {
                    const Icon = FILTER_ICON[d];
                    return (
                      <DropdownMenuSub key={d}>
                        <DropdownMenuSubTrigger>
                          <Icon className="mr-2 h-4 w-4 text-gray-500" /> {DIM_LABEL[d]}
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent className="max-h-80 overflow-y-auto">
                          <FilterValues query={query} dimension={d} onPick={(row) => setFilter(d, row)} />
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                    );
                  })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  setTo(today);
                  setFrom(toInputDate(new Date(Date.now() - (p.days - 1) * DAY_MS)));
                }}
                className={cn(
                  'rounded px-2 py-1 text-xs font-medium transition-colors',
                  activePreset === p.id ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-50',
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2 py-1">
            <input
              type="date"
              value={from}
              max={to}
              onChange={(e) => e.target.value && setFrom(e.target.value)}
              className="h-6 w-[118px] border-0 bg-transparent p-0 text-xs text-gray-700 outline-none"
              aria-label="Start date"
            />
            <span className="text-[10px] text-gray-400">→</span>
            <input
              type="date"
              value={to}
              min={from}
              max={today}
              onChange={(e) => e.target.value && setTo(e.target.value)}
              className="h-6 w-[118px] border-0 bg-transparent p-0 text-xs text-gray-700 outline-none"
              aria-label="End date"
            />
          </div>
        </div>
      </div>

      {/* Source tabs */}
      <div className="flex gap-6 border-b border-gray-200">
        {providers.isLoading && <Skeleton className="mb-2 h-6 w-72" />}
        {providers.data?.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => switchProvider(p.key)}
            className={cn(
              '-mb-px border-b-2 pb-2 text-sm font-medium',
              p.key === providerKey
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-gray-500 hover:text-gray-700',
            )}
          >
            {p.label}
            {!p.connected && <span className="ml-1.5 text-[11px] font-normal text-gray-400">not connected</span>}
          </button>
        ))}
      </div>

      {Object.keys(filters).length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {Object.entries(filters).map(([d, v]) => (
            <span
              key={d}
              className="inline-flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 py-1 pl-3 pr-1.5 text-xs text-indigo-800"
            >
              {DIM_LABEL[d]} is <strong className="font-semibold">{rowLabel(d, { name: v, value: v, visitors: 0 })}</strong>
              <button
                type="button"
                aria-label={`Remove ${DIM_LABEL[d]} filter`}
                onClick={() => setFilters(({ [d]: _removed, ...rest }) => rest)}
                className="rounded-full p-0.5 hover:bg-indigo-200"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <button type="button" onClick={() => setFilters({})} className="text-xs text-gray-500 hover:underline">
            Clear all
          </button>
        </div>
      )}

      {providers.error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {getApiErrorMessage(providers.error, 'Could not load Visitor Insights')}
        </div>
      ) : provider && !provider.connected ? (
        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center shadow-sm">
          <h2 className="text-base font-semibold text-gray-900">{provider.label} is not connected</h2>
          <p className="mx-auto mt-1 max-w-md text-sm text-gray-500">{provider.note}</p>
        </div>
      ) : summary.error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {getApiErrorMessage(summary.error, 'Could not load visitor figures')}
        </div>
      ) : provider ? (
        <>
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="grid grid-cols-2 divide-gray-100 border-b border-gray-100 md:grid-cols-3 xl:grid-cols-6 xl:divide-x">
              {METRICS.map((m) => {
                const now = summary.data?.totals[m.key];
                const before = summary.data?.previous[m.key];
                const change = now !== undefined && before ? Math.round(((now - before) / before) * 100) : null;
                const good = change !== null && (m.lowerIsBetter ? change <= 0 : change >= 0);
                return (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setMetric(m.key)}
                    className={cn('p-4 text-left transition-colors hover:bg-gray-50', metric === m.key && 'bg-indigo-50/60')}
                  >
                    <div
                      className={cn(
                        'text-[11px] font-semibold uppercase tracking-wide',
                        metric === m.key ? 'text-indigo-700' : 'text-gray-500',
                      )}
                    >
                      {m.label}
                    </div>
                    {summary.isLoading || now === undefined ? (
                      <Skeleton className="mt-2 h-7 w-20" />
                    ) : (
                      <div className="mt-1 flex items-baseline gap-2">
                        <span className="text-2xl font-bold text-gray-900">{m.fmt(now)}</span>
                        {change !== null && change !== 0 && (
                          <span
                            className={cn('inline-flex items-center text-xs font-medium', good ? 'text-emerald-600' : 'text-red-600')}
                            title="Against the same number of days before this range"
                          >
                            {change > 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                            {Math.abs(change)}%
                          </span>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="h-72 p-4">
              {summary.isLoading ? (
                <div className="flex h-full items-center justify-center gap-2 text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading {active.label.toLowerCase()} from {provider.label}
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chart} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="visitor-fill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#6366f1" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#6366f1" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9ca3af' }} minTickGap={24} />
                    <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} width={48} tickFormatter={active.fmt} />
                    <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v: number) => [active.fmt(v), active.label]} />
                    <Area type="monotone" dataKey="value" stroke="#6366f1" strokeWidth={2} fill="url(#visitor-fill)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            {PANELS.map((panel) => (
              <BreakdownCard key={panel.title} panel={panel} provider={provider} query={query} onFilter={setFilter} />
            ))}
          </div>

          {provider.journeys && <Journeys query={query} />}
        </>
      ) : null}
    </div>
  );
};

export default VisitorInsightsPage;
